import { create } from "zustand";
import {
  IEmailTemplate,
  ITextTemplate,
  IUpsertMessageTemplateCommand,
} from "../../../App/Typings/MessageText";
import { textMessageContainsLink } from "../../../MessageComponents/Constants/MessageConstants";

const maxTemplateNameLength = 100;
export const maxSubjectLength = 100;
export const maxEmailLength = 1500;
const maxEmailLengthDisplay = maxEmailLength.toLocaleString();
export const maxTextLength = 600;
export const maxTextSmsLength = 160;
const maxSubjectError = `Subject cannot be longer than ${maxSubjectLength} characters`;
const minSubjectLengthErrorMessage = "Subject cannot be empty";
const minBodyLengthErrorMessage = "Message cannot be empty";
const maxTemplateNameLengthError = `Name cannot be longer than ${maxTemplateNameLength} characters`;
const minTemplateNameLengthErrorMessage = "Name cannot be empty";
const getMaxMessageLength = (isEmail: boolean) => (isEmail ? maxEmailLength : maxTextLength);
const getMaxMessageLengthError = (isEmail: boolean) =>
  `Message cannot be longer than ${isEmail ? maxEmailLengthDisplay : maxTextLength} characters`;

export const generateBodyErrorMessage = (incomingMessageBody: string, isEmail: boolean) => {
  const messageLength = incomingMessageBody.trim().length;
  const maxMessageLength = getMaxMessageLength(isEmail);
  const maxMessageLengthError = getMaxMessageLengthError(isEmail);

  let bodyErrorMessage = undefined;

  if (messageLength > maxMessageLength) {
    // CRM-485 Remove max length validation for Email messages until Large Email Templates are implemented
    // bodyErrorMessage = maxMessageLengthError;
    if (!isEmail) {
      bodyErrorMessage = maxMessageLengthError;
    }
  } else if (messageLength === 0) {
    bodyErrorMessage = minBodyLengthErrorMessage;
  }

  return bodyErrorMessage;
};

const AllowedVisibility = ["Public", "Private"] as const;
const Fields = ["name", "body", "subject", "visibility"] as const;
type MessageTemplateDraftFields = (typeof Fields)[number];
type DraftMessageTemplate = {
  name: string;
  body: string;
  bodyText?: string;
  subject: string;
  visibility: "Public" | "Private";
} & Partial<IEmailTemplate | ITextTemplate>;

interface IErrorMessage {
  message: string;
  key: MessageTemplateDraftFields;
}

interface IMessageTemplateStoreState {
  action: "create" | "edit" | "duplicate";
  disableSaveMessage?: string;
  draftFieldsTouched: Record<MessageTemplateDraftFields, boolean>;
  draftTemplate: DraftMessageTemplate;
  errors: Record<MessageTemplateDraftFields, string>;
  template?: IEmailTemplate | ITextTemplate;
  textSplitCount?: number;
  textSplitWarning?: string;
  textContainsLink?: boolean;
  type: "email" | "text";
}

interface IMessageTemplateStoreActions {
  clearDraft: () => void;
  editDraft: (draft: Partial<DraftMessageTemplate>) => DraftMessageTemplate;
  createDraft: (template?: IEmailTemplate | ITextTemplate) => void;
  createDuplicate: (template?: IEmailTemplate | ITextTemplate) => void;
  createUpsertCommand: () => IUpsertMessageTemplateCommand;
  scanDraftForChangesSetTouched: () => boolean;
  setAction: (action: "create" | "edit") => void;
  setErrorMessage: (error: IErrorMessage) => void;
  setType: (type: "email" | "text") => void;
  validateTemplateSave: () => string | undefined;
}

interface IDraftMessageTemplateStore extends IMessageTemplateStoreState, IMessageTemplateStoreActions {}

const initialMessageTemplateState = {
  action: "create",
  draftFieldsTouched: {
    name: false,
    body: false,
    subject: false,
    visibility: false,
  },
  draftTemplate: {
    body: "",
    bodyText: "",
    name: "",
    subject: "",
    visibility: "Public",
  },
  errors: {
    body: "",
    name: "",
    subject: "",
    visibility: "",
  },
  type: "email",
} as IMessageTemplateStoreState;

export const useDraftMessageTemplateStore = create<IDraftMessageTemplateStore>((set, get) => ({
  ...initialMessageTemplateState,
  clearDraft() {
    set((state) => ({
      ...initialMessageTemplateState,
      type: state.type,
      action: state.action,
      disableSaveMessage: "No changes to save",
      template: undefined,
      textSplitCount: undefined,
      textSplitWarning: undefined,
      textContainsLink: undefined,
    }));
  },
  createUpsertCommand() {
    const state = get();
    const isEmail = state.type === "email";
    const draft = state.draftTemplate;
    const templateId =
      (state.template as IEmailTemplate | undefined)?.emailTemplateId ??
      (state.template as ITextTemplate | undefined)?.textTemplateId;

    return {
      templateId: templateId && templateId !== "" ? templateId : undefined,
      body: draft.body,
      subject: isEmail ? draft.subject : undefined,
      name: draft.name,
      visibility: draft.visibility,
      uiComponent: "Messaging Center",
      type: state.type,
    };
  },
  editDraft(draft) {
    const state = get();
    const pendingErrors = {} as Record<MessageTemplateDraftFields, string>;
    const pendingTouched = {} as Record<MessageTemplateDraftFields, boolean>;
    let pendingTextSplitCount = state.textSplitCount;
    let pendingTextSplitWarning = state.textSplitWarning;
    let pendingTextContainsLink = state.textContainsLink;
    const isEmail = state.type === "email";
    const maxMessageLength = getMaxMessageLength(isEmail);
    const maxMessageLengthError = getMaxMessageLengthError(isEmail);
    const touchedKeys = (Object.keys(draft) as MessageTemplateDraftFields[]).filter((key) =>
      Fields.includes(key),
    );
    touchedKeys.forEach((field) => {
      pendingTouched[field] = true;
      if (field === "name" && draft.name !== undefined) {
        if (draft.name.trim().length > maxTemplateNameLength) {
          pendingErrors[field] = maxTemplateNameLengthError;
        } else if (draft.name.trim().length === 0) {
          pendingErrors[field] = minTemplateNameLengthErrorMessage;
        } else {
          pendingErrors[field] = "";
        }
      }
      if (field === "body" && !isEmail && draft.body !== undefined) {
        if (draft.body.trim().length > maxMessageLength) {
          pendingErrors[field] = maxMessageLengthError;
        } else if (draft.body.trim().length === 0) {
          pendingErrors[field] = minBodyLengthErrorMessage;
        } else {
          pendingErrors[field] = "";
        }
        if (draft.body.trim().length > maxTextSmsLength) {
          pendingTextSplitCount = Math.min(Math.ceil(draft.body.length / maxTextSmsLength), 4);
          pendingTextSplitWarning = `Messages will be split into ${pendingTextSplitCount} messages, each will be treated as a separate text message in your quota.`;
        } else {
          pendingTextSplitCount = undefined;
          pendingTextSplitWarning = undefined;
        }
        pendingTextContainsLink = Boolean(textMessageContainsLink(draft.body, false));
      }
      // CRM-485 Do not require max length validation for Email messages until Large Email Templates are implemented
      if (field === "body" && isEmail && draft.body !== undefined) {
        if (draft.body.trim().length === 0 || draft.bodyText?.trim().length === 0) {
          pendingErrors[field] = minBodyLengthErrorMessage;
        } else {
          pendingErrors[field] = "";
        }
        pendingTouched[field] =
          state.draftFieldsTouched.body ||
          draft.body.length > 0 ||
          (draft.bodyText !== undefined && draft.bodyText.length > 0);
      }
      if (field === "subject" && isEmail && draft.subject !== undefined) {
        if (draft.subject.trim().length > maxSubjectLength) {
          pendingErrors[field] = maxSubjectError;
        } else if (draft.subject.trim().length === 0) {
          pendingErrors[field] = minSubjectLengthErrorMessage;
        } else {
          pendingErrors[field] = "";
        }
      }
      if (field === "visibility" && draft.visibility !== undefined) {
        if (!AllowedVisibility.includes(draft.visibility)) {
          pendingErrors[field] = "Invalid visibility";
        } else {
          pendingErrors[field] = "";
        }
      }
    });

    set((state) => ({
      draftTemplate: { ...state.draftTemplate, ...draft },
      errors: { ...state.errors, ...pendingErrors },
      draftFieldsTouched: { ...state.draftFieldsTouched, ...pendingTouched },
      textSplitCount: pendingTextSplitCount,
      textSplitWarning: pendingTextSplitWarning,
      textContainsLink: pendingTextContainsLink,
    }));

    get().scanDraftForChangesSetTouched();
    get().validateTemplateSave();
    return get().draftTemplate;
  },
  createDraft(template) {
    set(() => ({
      action: template ? "edit" : "create",
      draftTemplate: template
        ? {
            body: template.body,
            name: template.name,
            subject: (template as IEmailTemplate).subject ?? "",
            visibility: template.visibility === "Private" ? "Private" : "Public",
          }
        : initialMessageTemplateState.draftTemplate,
      disableSaveMessage: "No changes to save",
      template,
    }));
  },
  createDuplicate(template) {
    set(() => ({
      action: "duplicate",
      draftTemplate: template
        ? {
            body: template.body,
            name: template.name,
            subject: (template as IEmailTemplate).subject ?? "",
            visibility: template.visibility === "Private" ? "Private" : "Public",
          }
        : initialMessageTemplateState.draftTemplate,
      disableSaveMessage: undefined,
      template,
    }));
  },
  scanDraftForChangesSetTouched() {
    const state = get();
    if (state.action !== "duplicate") {
      const draft = state.draftTemplate;
      const template = state.template;
      const isEmail = state.type === "email";
      const hasBodyChange =
        draft.body !== undefined && draft.body.length > 0 && draft.body !== template?.body;
      const hasNameChange =
        draft.name !== undefined && draft.name.length > 0 && draft.name !== template?.name;
      const hasSubjectChange = isEmail ? draft.subject !== (template as IEmailTemplate)?.subject : false;
      const hasVisibilityChange = draft.visibility !== template?.visibility;
      set((state) => ({
        draftFieldsTouched: {
          body: state.draftFieldsTouched.body || hasBodyChange,
          name: state.draftFieldsTouched.name || hasNameChange,
          subject: state.draftFieldsTouched.subject || hasSubjectChange,
          visibility: state.draftFieldsTouched.visibility || hasVisibilityChange,
        },
      }));
      return hasNameChange || hasVisibilityChange || hasBodyChange || hasSubjectChange;
    }

    return true;
  },
  setAction(action) {
    set(() => ({ action }));
  },
  setErrorMessage({ message, key }) {
    set((state) => ({
      errors: {
        ...state.errors,
        [key]: message,
      },
    }));
  },
  setType(type) {
    set(() => ({ type }));
  },
  validateTemplateSave() {
    const state = get();
    const {
      type,
      errors,
      scanDraftForChangesSetTouched,
      draftTemplate: { name, subject, body, bodyText },
    } = state;
    const hasErrors = Object.values(errors).some((error) => error.length > 0);
    const hasNoChanges = !scanDraftForChangesSetTouched();
    const hasMissingValues =
      name?.trim().length === 0 ||
      (type === "email"
        ? subject?.trim().length === 0 || body.trim().length === 0 || bodyText?.trim().length === 0
        : body?.trim().length === 0);
    const disableSaveMessage = hasErrors
      ? "Please correct the errors before saving"
      : hasNoChanges
        ? "No changes to save"
        : hasMissingValues
          ? "Please fill in all required fields"
          : undefined;
    set(() => ({ disableSaveMessage }));
    return disableSaveMessage;
  },
}));

export const selectDraftModalState = (state: IDraftMessageTemplateStore) => ({
  disableSaveMessage: state.disableSaveMessage,
  clearDraft: state.clearDraft,
  createDraft: state.createDraft,
  createDuplicate: state.createDuplicate,
  createUpsertCommand: state.createUpsertCommand,
  setType: state.setType,
  validateTemplateSave: state.validateTemplateSave,
});

export const selectDraftType = (state: IDraftMessageTemplateStore) => ({
  type: state.type,
  setType: state.setType,
});

export const selectEditDraft = (state: IDraftMessageTemplateStore) => ({
  action: state.action,
  disableSaveMessage: state.disableSaveMessage,
  clearDraft: state.clearDraft,
  createUpsertCommand: state.createUpsertCommand,
});

export const selectCreateDraft = (state: IDraftMessageTemplateStore) => ({
  createDraft: state.createDraft,
});

export const selectEditName = (state: IDraftMessageTemplateStore) => ({
  name: state.draftTemplate.name,
  nameErrorMessage: state.errors.name,
  nameTouched: state.draftFieldsTouched.name,
  editDraft: state.editDraft,
});

export const selectEditVisibility = (state: IDraftMessageTemplateStore) => ({
  visibility: state.draftTemplate.visibility,
  editDraft: state.editDraft,
});

export const selectEditEmailBody = (state: IDraftMessageTemplateStore) => ({
  body: state.draftTemplate.body,
  bodyText: state.draftTemplate.bodyText,
  bodyErrorMessage: state.errors.body,
  bodyTouched: state.draftFieldsTouched.body,
  editDraft: state.editDraft,
  setErrorMessage: state.setErrorMessage,
});

export const selectEditTextBody = (state: IDraftMessageTemplateStore) => ({
  body: state.draftTemplate.body,
  bodyErrorMessage: state.errors.body,
  bodyTouched: state.draftFieldsTouched.body,
  textSplitCount: state.textSplitCount,
  textSplitWarning: state.textSplitWarning,
  textContainsLink: state.textContainsLink,
  editDraft: state.editDraft,
  setErrorMessage: state.setErrorMessage,
});

export const selectEditSubject = (state: IDraftMessageTemplateStore) => ({
  subject: state.draftTemplate.subject,
  subjectErrorMessage: state.errors.subject,
  subjectTouched: state.draftFieldsTouched.subject,
  editDraft: state.editDraft,
});
